<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  content: { type: Object, required: true },
  prefix:  { type: String, default: '' },
})
const c = props.content
</script>

<template>
  <Head>
    <title>{{ c.title }}</title>
    <meta name="description" :content="c.meta_description">
  </Head>

  <BrandHero kind="delivery" :eyebrow="c.hero.eyebrow" :title="c.hero.headline">
    <p class="dl-hero__sub">{{ c.hero.sub }}</p>
    <p class="dl-hero__honesty">{{ c.hero.honesty }}</p>
  </BrandHero>

  <section class="dl-body">
    <ol class="dl-steps">
      <li v-for="(s, i) in c.steps" :key="i" class="dl-step">
        <span class="dl-step__n fw-value">{{ i + 1 }}</span>
        <div>
          <h2 class="dl-step__title">{{ s.title }}</h2>
          <p class="dl-step__body">{{ s.body }}</p>
        </div>
      </li>
    </ol>

    <div class="dl-card">
      <h2 class="dl-heading">{{ c.coverage.heading }}</h2>
      <p class="dl-prose">{{ c.coverage.body }}</p>
      <ul class="dl-areas">
        <li v-for="a in c.coverage.areas" :key="a">{{ a }}</li>
      </ul>
    </div>

    <div class="dl-card">
      <h2 class="dl-heading">{{ c.why.heading }}</h2>
      <div class="dl-why">
        <div v-for="(w, i) in c.why.items" :key="i" class="dl-why__item">
          <h3 class="dl-why__title">{{ w.title }}</h3>
          <p class="dl-prose">{{ w.body }}</p>
        </div>
      </div>
    </div>

    <div class="dl-card">
      <h2 class="dl-heading">{{ c.faq.heading }}</h2>
      <details v-for="(f, i) in c.faq.items" :key="i" class="dl-faq">
        <summary class="dl-faq__q">{{ f.q }}</summary>
        <p class="dl-faq__a dl-prose">{{ f.a }}</p>
      </details>
    </div>

    <div class="dl-cta">
      <h2 class="dl-cta__title">{{ c.cta.title }}</h2>
      <LuxButton :href="`${prefix}/rentals`" variant="solid" tone="light" glyph="arrow">{{ c.cta.button }}</LuxButton>
    </div>
  </section>
</template>

<style scoped>
/* Delivery — a benefit page for rentals, set in the same system as the rest. */
.dl-hero__sub { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 54ch; opacity: 0.94; }
.dl-hero__honesty { margin: 0.6rem 0 0; font-size: 0.85rem; opacity: 0.8; }
.dl-body { max-width: 64rem; margin: 0 auto; padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 4vw, 2rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 3.5rem); }
.dl-steps { list-style: none; margin: 0; padding: 0; }
.dl-step { display: grid; grid-template-columns: 3rem 1fr; gap: 1rem; padding: 1rem 0; border-top: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.dl-step:last-child { border-bottom: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.dl-step__n { font-family: var(--font-display, var(--display)); font-size: 1.5rem; line-height: 1; color: var(--gold); padding-top: 0.15rem; }
.dl-step__title { margin: 0 0 0.25rem; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.45rem; line-height: 1.1; color: var(--ink); }
.dl-step__body, .dl-prose { margin: 0; color: var(--inkSoft); line-height: 1.6; }
.dl-card { background: var(--surface); border: 1px solid color-mix(in oklch, var(--gold) 40%, var(--line)); border-radius: 1.25rem; padding: 1.5rem; }
.dl-heading { margin: 0 0 0.75rem; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.6rem, 2.6vw, 2.1rem); line-height: 1.08; color: var(--ink); text-wrap: balance; }
.dl-heading::before { content: ""; display: block; width: 2rem; height: 1px; background: var(--gold); margin-bottom: 0.8rem; }
.dl-areas { list-style: none; margin: 1rem 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.dl-areas li { padding: 0.35rem 0.8rem; border-radius: 999px; font-size: 0.85rem; background: var(--surface); border: 1px solid color-mix(in oklch, var(--gold) 60%, var(--line)); color: var(--ink); }
.dl-why { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.25rem 2rem; margin-top: 0.5rem; }
.dl-why__item::before { content: ""; display: block; width: 1.6rem; height: 1px; background: var(--gold); margin-bottom: 0.6rem; }
.dl-why__title { margin: 0 0 0.25rem; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: 1.3rem; line-height: 1.15; color: var(--ink); }
.dl-faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.dl-faq:first-of-type { border-top: none; }
.dl-faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.dl-faq__a { margin-top: 0.5rem; }
.dl-cta { text-align: center; padding: 1.5rem 0 0; display: grid; gap: 1rem; justify-items: center; }
.dl-cta__title { margin: 0; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.7rem, 2.8vw, 2.3rem); line-height: 1.08; color: var(--ink); }
</style>
